import React, { useEffect, useReducer, useRef, useState } from 'react';

// Alle paar Sekunden schiebt sich ein Otter hinter einer Kante hervor, schaut
// kurz zu und verschwindet wieder. Erlaubt sind nur Verstecke, an denen die
// abgeschnittene Kante des Bildes unsichtbar bleibt: hinter einer weissen
// Karte, seitlich aus dem Fenster oder unterhalb der Falz.
//
// Bei den seitlichen Auftritten liegt der Otter um 90 Grad gedreht. Dadurch
// zeigt der Anschnitt am unteren Bildrand zur Seite und bleibt hinter der
// Karte bzw. dem Fensterrand verborgen, waehrend der Kopf voran hereinschaut.
//
// WICHTIG: Die Komponente muss innerhalb des zentrierten Inhaltscontainers
// stehen. Die Karten messen ihr offsetTop gegen ihn, und die Karten-Auftritte
// positionieren sich mit genau diesen Koordinaten.

const OTTERS = [
  process.env.PUBLIC_URL + '/otters/manyeyes.png',
  process.env.PUBLIC_URL + '/otters/binoculars.png',
];

const SIZE = 156; // Kantenlaenge des Otters
const BAND = 130; // Hoehe, die ein Versteck mindestens freihalten muss
const PAGE_MAX = 1136; // Breite des Inhaltscontainers, siehe ViewerView
const GUTTER = 64; // dessen seitliches Padding

// Der erste Auftritt wartet, bis die Daten vom Server da sind: vorher steht
// die Rangliste noch leer und alle Karten sitzen an ganz anderen Stellen.
const SETTLE_MS = 2200;

// Wie weit darf der Otter an seiner Kante entlangwandern -- und wohin?
// Die meisten Auftritte sind ein stilles Hervorlugen ohne Drift.
const pickDrift = (room1, room2) => {
  if (Math.random() < 0.55) return 0;
  const down = Math.random() < 0.5;
  const room = Math.max(0, (down ? room2 : room1) - 8);
  const d = Math.min(60 + Math.random() * 130, room);
  return d < 24 ? 0 : Math.round(down ? d : -d);
};

const edgePlace = (side) => {
  const y = 90 + Math.random() * Math.max(120, window.innerHeight - 300);
  return { mode: 'edge', side, y, drift: pickDrift(y - 40, window.innerHeight - 150 - y) };
};

const pickPlace = (cards) => {
  const modes = ['bottom', 'edge', 'edge'];
  if (cards.length) modes.push('card', 'card');
  const mode = modes[Math.floor(Math.random() * modes.length)];
  const side = Math.random() < 0.5 ? 'L' : 'R';
  let o;

  if (mode === 'card') {
    // Nur in dem Teil einer Karte, der gerade wirklich auf dem Schirm ist.
    const sy = window.scrollY;
    const vh = window.innerHeight;
    const bands = cards
      .map((el) => ({
        lo: Math.max(el.offsetTop + 14, sy + 24),
        hi: Math.min(el.offsetTop + el.offsetHeight - BAND - 14, sy + vh - BAND - 24),
      }))
      .filter((b) => b.hi > b.lo);
    if (bands.length) {
      const b = bands[Math.floor(Math.random() * bands.length)];
      const y = b.lo + Math.random() * (b.hi - b.lo);
      o = { mode: 'card', side, y, drift: pickDrift(y - b.lo, b.hi - y) };
    } else {
      o = edgePlace(side);
    }
  } else if (mode === 'edge') {
    o = edgePlace(side);
  } else {
    // Unten nur in der Wiese: links oder rechts neben den Karten -- oder
    // ueberall, wenn an der Falz gerade keine Karte im Weg steht.
    const covered = cards.some((c) => {
      const r = c.getBoundingClientRect();
      return r.top < window.innerHeight - 40 && r.bottom > window.innerHeight - 150;
    });
    if (covered) {
      const r = cards[0].getBoundingClientRect();
      const leftBand = r.left - 24;
      const rightBand = window.innerWidth - r.right - 24;
      const band = side === 'L' ? leftBand : rightBand;
      if (band < 70) {
        o = edgePlace(side);
      } else {
        const px =
          side === 'L'
            ? Math.random() * (leftBand - 60)
            : r.right + 12 + Math.random() * (rightBand - 60);
        const lo = side === 'L' ? 0 : r.right + 12;
        const hi = side === 'L' ? r.left - 24 - 60 : window.innerWidth - 84;
        o = {
          mode: 'bottom',
          side,
          x: (px / window.innerWidth) * 100,
          drift: pickDrift(px - lo, hi - px),
        };
      }
    } else {
      const bx = 6 + Math.random() * 80;
      const w = window.innerWidth;
      o = {
        mode: 'bottom',
        side,
        x: bx,
        drift: pickDrift((bx / 100) * w - 10, ((94 - bx) / 100) * w),
      };
    }
  }

  o.dur = Number((o.drift ? 4.2 + Math.random() * 4.6 : 2.6 + Math.random() * 3.8).toFixed(2));
  return o;
};

const layout = (o) => {
  const dur = o.dur || 5.4;
  const drift = (axis) =>
    o.drift
      ? {
          '--drift': Math.round(o.drift) + 'px',
          animation: 'otterDrift' + axis + ' ' + dur + 's ease-in-out both',
        }
      : null;
  const base = { zIndex: 1, pointerEvents: 'none' };

  if (o.mode === 'bottom') {
    return {
      wrap: {
        ...base,
        position: 'fixed',
        width: SIZE,
        height: SIZE,
        bottom: -(SIZE + 16),
        left: o.x + 'vw',
        ...drift('X'),
      },
      anim: 'otterPeekUp',
      peek: SIZE - 18,
      dur,
    };
  }

  if (o.mode === 'edge') {
    return {
      wrap: {
        ...base,
        position: 'fixed',
        width: SIZE,
        height: SIZE,
        top: Math.round(o.y),
        [o.side === 'L' ? 'left' : 'right']: -SIZE,
        ...drift('Y'),
      },
      anim: o.side === 'L' ? 'otterPeekRight' : 'otterPeekLeft',
      peek: SIZE - 18,
      dur,
    };
  }

  // Hinter einer Karte: der Otter darf hoechstens so weit heraus, wie die
  // Randspalte breit ist -- sonst waere seine Schnittkante zu sehen.
  const w = window.innerWidth;
  const gutter = (w - Math.min(PAGE_MAX, w)) / 2 + GUTTER;
  const peek = Math.max(30, Math.min(gutter - 8, SIZE - 18));
  const size = Math.max(101, Math.min(SIZE, peek * 1.8));
  return {
    wrap: {
      ...base,
      position: 'absolute',
      width: size,
      height: size,
      top: Math.round(o.y),
      [o.side === 'L' ? 'left' : 'right']: GUTTER,
      ...drift('Y'),
    },
    anim: o.side === 'L' ? 'otterPeekLeft' : 'otterPeekRight',
    peek,
    dur,
  };
};

// anchors: Refs auf die weissen Karten, hinter denen sich der Otter
// verstecken darf.
const OtterPeek = ({ anchors }) => {
  const [otter, setOtter] = useState(null);
  const [, bump] = useReducer((x) => x + 1, 0);
  const anchorsRef = useRef(anchors);
  const timer = useRef(null);
  anchorsRef.current = anchors;

  useEffect(() => {
    const reduced =
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced) return undefined;

    const next = () => {
      const cards = (anchorsRef.current || []).map((r) => r && r.current).filter(Boolean);
      const place = pickPlace(cards);
      setOtter((prev) => ({
        place,
        index: prev
          ? (prev.index + 1) % OTTERS.length
          : Math.floor(Math.random() * OTTERS.length),
        cycle: (prev ? prev.cycle : 0) + 1,
      }));
    };
    const schedule = (delay) => {
      timer.current = setTimeout(() => {
        next();
        schedule(9000 + Math.random() * 12000);
      }, delay);
    };

    schedule(SETTLE_MS);
    window.addEventListener('resize', bump);
    return () => {
      clearTimeout(timer.current);
      window.removeEventListener('resize', bump);
    };
  }, []);

  if (!otter) return null;
  const L = layout(otter.place);

  return (
    <div key={'otter-wrap-' + otter.cycle} style={L.wrap}>
      <div
        key={'otter-' + otter.cycle}
        style={{
          width: '100%',
          height: '100%',
          '--peek': L.peek + 'px',
          animation: L.anim + ' ' + L.dur + 's ease-in-out both',
        }}
      >
        <img
          src={OTTERS[otter.index]}
          alt=""
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'contain',
            filter: 'drop-shadow(0 8px 14px rgba(30,60,25,.28))',
          }}
        />
      </div>
    </div>
  );
};

export default OtterPeek;
